/**
 * AdminClientErrors — /admin/client-errors
 *
 * Operator brief (Feb 19, 2026):
 *   "If a user reports ERR-XXXXXX, I need to paste that in and instantly
 *    see what happened without manual curl/API work."
 *
 * Doctrine:
 *   • Read-only. Zero mutations.
 *   • Calm slate palette — categories rendered as muted chips, never
 *     red panic surfaces.
 *   • Stack traces visible here (admin-only by route guard + backend
 *     admin-only GET); never to normal production users.
 *   • Uses the existing GET endpoints already in production:
 *       GET /api/debug/client-errors             (list + filters)
 *       GET /api/debug/client-errors/{ref_id}    (detail)
 *       GET /api/debug/client-errors/_categories (vocabulary)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertOctagon, RefreshCw, Search, X, Copy, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;


function categoryChipClass(cat) {
  switch (cat) {
    case "render_crash":
    case "chunk_load":
    case "unhandled_promise":
      return "bg-slate-100 text-slate-700 border-slate-200";
    case "server_5xx":
    case "timeout":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "network":
      return "bg-sky-50 text-sky-700 border-sky-200";
    case "auth":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "stale_active_operation":
      return "bg-violet-50 text-violet-700 border-violet-200";
    case "client_4xx":
      return "bg-slate-50 text-slate-600 border-slate-200";
    default:
      return "bg-slate-50 text-slate-500 border-slate-200";
  }
}


function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}


export default function AdminClientErrors() {
  const [filters, setFilters] = useState({
    ref_id: "", category: "", route: "", since: "",
  });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Vocabulary
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/debug/client-errors/_categories`, {
      withCredentials: true,
    }).then((r) => {
      if (!cancelled) setCategories(r.data?.categories || []);
    }).catch(() => { /* admin route — page-level error covers it */ });
    return () => { cancelled = true; };
  }, []);

  const params = useMemo(() => {
    const p = { limit: 100 };
    if (filters.ref_id.trim())  p.ref_id = filters.ref_id.trim().toUpperCase();
    if (filters.category)        p.category = filters.category;
    if (filters.route.trim())    p.route = filters.route.trim();
    if (filters.since)           p.since = new Date(filters.since).toISOString();
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/debug/client-errors`, {
        params, withCredentials: true,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load reports just now.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Initial load + reload when filters change (debounced via a small delay).
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openDetail = useCallback(async (refId) => {
    if (!refId) return;
    setDetailLoading(true);
    setSelected({ ref_id: refId, loading: true });
    try {
      const { data } = await axios.get(
        `${API}/api/debug/client-errors/${encodeURIComponent(refId)}`,
        { withCredentials: true },
      );
      setSelected(data);
    } catch (e) {
      setSelected({
        ref_id: refId,
        _error: e?.response?.data?.detail || "Couldn't fetch this record.",
      });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const clearFilters = () => setFilters({
    ref_id: "", category: "", route: "", since: "",
  });

  return (
    <div
      data-testid="admin-client-errors"
      className="min-h-dvh bg-slate-50 px-4 py-6"
    >
      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <AlertOctagon className="w-5 h-5 text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-800">
              Client Error Reports
            </h1>
          </div>
          <p className="text-sm text-slate-500">
            Search by reference ID, category, route or date. Read-only —
            this view never sends or modifies anything.
          </p>
        </header>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FilterField label="Reference ID">
              <input
                type="text"
                data-testid="admin-client-errors-filter-ref"
                value={filters.ref_id}
                onChange={(e) => setFilters((f) => ({ ...f, ref_id: e.target.value }))}
                placeholder="ERR-XXXXXX"
                className="w-full text-sm rounded-md border border-slate-200 px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400 font-mono uppercase"
              />
            </FilterField>
            <FilterField label="Category">
              <select
                data-testid="admin-client-errors-filter-category"
                value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                className="w-full text-sm rounded-md border border-slate-200 px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Route contains">
              <input
                type="text"
                data-testid="admin-client-errors-filter-route"
                value={filters.route}
                onChange={(e) => setFilters((f) => ({ ...f, route: e.target.value }))}
                placeholder="/work-mode"
                className="w-full text-sm rounded-md border border-slate-200 px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </FilterField>
            <FilterField label="Since">
              <input
                type="datetime-local"
                data-testid="admin-client-errors-filter-since"
                value={filters.since}
                onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))}
                className="w-full text-sm rounded-md border border-slate-200 px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
              />
            </FilterField>
          </div>

          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              data-testid="admin-client-errors-refresh"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-60 rounded-md px-3 py-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              data-testid="admin-client-errors-clear"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              <X className="w-3.5 h-3.5" />
              Clear filters
            </button>
            <span className="ml-auto text-[12px] text-slate-500">
              {loading ? "Loading…" : `${items.length} report${items.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
          {error ? (
            <p
              data-testid="admin-client-errors-error"
              className="text-sm text-slate-600 px-4 py-6"
            >
              {String(error)}
            </p>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <p
              data-testid="admin-client-errors-empty"
              className="text-sm text-slate-500 px-4 py-10 text-center"
            >
              No reports match these filters.
            </p>
          ) : null}

          {items.map((row) => (
            <button
              type="button"
              key={row.ref_id}
              data-testid={`admin-client-errors-row-${row.ref_id}`}
              onClick={() => openDetail(row.ref_id)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 focus:outline-none focus:bg-slate-50"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[12px] font-mono font-semibold text-slate-800">
                  {row.ref_id}
                </span>
                <span className={`text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded-md border ${categoryChipClass(row.category)}`}>
                  {(row.category || "unknown").replace(/_/g, " ")}
                </span>
                {typeof row.request_status === "number" ? (
                  <span className="text-[11px] font-mono text-slate-500">
                    {row.request_status}
                  </span>
                ) : null}
                <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3 opacity-70" />
                  {fmtWhen(row.created_at)}
                </span>
                <span className="text-[11px] text-slate-500 truncate max-w-[40ch]">
                  {row.route || "—"}
                </span>
              </div>
              {row.message ? (
                <p className="mt-1 text-[12px] text-slate-600 truncate">
                  {row.message}
                </p>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {selected ? (
        <DetailDrawer
          selected={selected}
          loading={detailLoading}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}


function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}


function DetailDrawer({ selected, loading, onClose }) {
  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(selected.ref_id);
    } catch (_) { /* no clipboard — ignore */ }
  };
  return (
    <div
      className="fixed inset-0 z-40 flex"
      role="dialog"
      data-testid="admin-client-errors-detail"
    >
      <div
        className="flex-1 bg-slate-900/30"
        onClick={onClose}
      />
      <aside className="w-full max-w-xl bg-white shadow-xl overflow-y-auto p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">
              Client error record
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                data-testid="admin-client-errors-detail-ref"
                className="text-lg font-semibold font-mono text-slate-800"
              >
                {selected.ref_id}
              </h2>
              <button
                type="button"
                data-testid="admin-client-errors-detail-copy"
                onClick={copyRef}
                className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                title="Copy reference"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
              {selected.category ? (
                <span className={`text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded-md border ${categoryChipClass(selected.category)}`}>
                  {String(selected.category).replace(/_/g, " ")}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            data-testid="admin-client-errors-detail-close"
            onClick={onClose}
            className="ml-auto text-slate-500 hover:text-slate-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {selected._error ? (
          <p className="text-sm text-slate-600">{String(selected._error)}</p>
        ) : loading || selected.loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="space-y-3">
            <DetailRow label="When">{fmtWhen(selected.created_at)}</DetailRow>
            <DetailRow label="Environment">{selected.environment || "—"}</DetailRow>
            <DetailRow label="App version">{selected.app_version || "—"}</DetailRow>
            <DetailRow label="Route">{selected.route || "—"}</DetailRow>
            <DetailRow label="Component">{selected.component || "—"}</DetailRow>
            <DetailRow label="Request">
              {selected.request_method
                ? `${selected.request_method} ${selected.request_url || ""}`
                : (selected.request_url || "—")}
              {typeof selected.request_status === "number"
                ? ` · ${selected.request_status}` : ""}
            </DetailRow>
            <DetailRow label="User">
              {selected.user_id
                ? `${selected.user_role || "user"} · ${selected.user_id}`
                : "anonymous"}
            </DetailRow>
            <DetailRow label="Viewport">{selected.viewport || "—"}</DetailRow>
            <DetailRow label="Moving state">{selected.moving_state || "—"}</DetailRow>
            <DetailRow label="Message">
              <span className="whitespace-pre-wrap break-words">
                {selected.message || "—"}
              </span>
            </DetailRow>

            {selected.stack ? (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  Stack
                </div>
                <pre
                  data-testid="admin-client-errors-detail-stack"
                  className="text-[10px] leading-tight bg-slate-50 border border-slate-200 rounded-md p-2 max-h-72 overflow-auto whitespace-pre-wrap break-all text-slate-700"
                >{selected.stack}</pre>
              </div>
            ) : null}

            {selected.extras ? (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  Extras
                </div>
                <pre
                  className="text-[10px] leading-tight bg-slate-50 border border-slate-200 rounded-md p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all text-slate-700"
                >{JSON.stringify(selected.extras, null, 2)}</pre>
              </div>
            ) : null}

            {selected.active_operation_snapshot ? (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  Active operation snapshot
                </div>
                <pre
                  className="text-[10px] leading-tight bg-slate-50 border border-slate-200 rounded-md p-2 max-h-48 overflow-auto whitespace-pre-wrap break-all text-slate-700"
                >{JSON.stringify(selected.active_operation_snapshot, null, 2)}</pre>
              </div>
            ) : null}

            <DetailRow label="User agent">
              <span className="break-words text-[11px] text-slate-500">
                {selected.user_agent || "—"}
              </span>
            </DetailRow>
            <DetailRow label="IP hash">
              <span className="font-mono text-[11px] text-slate-500">
                {selected.ip_hash || "—"}
              </span>
            </DetailRow>
          </div>
        )}
      </aside>
    </div>
  );
}


function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-[12px]">
      <div className="uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-700">{children}</div>
    </div>
  );
}
